import React, { useState, useEffect, useCallback } from 'react';
import axios from '../../utils/axios';
import toast from 'react-hot-toast';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  PhotoIcon,
  CheckCircleIcon,
  GlobeAltIcon,
  BuildingOfficeIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import BrandFormModal from '../../components/admin/BrandFormModal';
import DeleteBrandConfirmation from '../../components/admin/DeleteBrandConfirmation';

const AdminBrands = () => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState('all'); // all, active, inactive
  const [filterFeatured, setFilterFeatured] = useState('all'); // all, featured, normal
  const [selectedCountry, setSelectedCountry] = useState('');
  const [countries, setCountries] = useState([]);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20
  });

  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState(null);

  useEffect(() => {
    fetchBrands();
    fetchFilters();
  }, [searchTerm, filterActive, filterFeatured, selectedCountry, pagination.currentPage]);

  const fetchBrands = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.currentPage,
        limit: 20,
        sort: 'name',
        order: 'asc'
      };

      if (searchTerm) params.search = searchTerm;
      if (filterActive !== 'all') params.is_active = filterActive === 'active';
      if (filterFeatured !== 'all') params.is_featured = filterFeatured === 'featured';
      if (selectedCountry) params.country = selectedCountry;

      const response = await axios.get('/api/brands', { params });
      setBrands(response.data.brands);
      setPagination(response.data.pagination);
    } catch (error) {
      toast.error('שגיאה בטעינת ברנדים');
    } finally {
      setLoading(false);
    }
  };

  const fetchFilters = async () => {
    try {
      const response = await axios.get('/api/brands/filters');
      setCountries(response.data.countries || []);
      setStats(response.data.stats);
    } catch (error) {
      console.error('Error fetching filters:', error);
    }
  };

  const handleAddBrand = () => {
    setSelectedBrand(null);
    setShowAddModal(true);
  };

  const handleEditBrand = (brand) => {
    setSelectedBrand(brand);
    setShowEditModal(true);
  };

  const handleDeleteBrand = (brand) => {
    setSelectedBrand(brand);
    setShowDeleteModal(true);
  };

  const handleBrandCreated = (newBrand) => {
    setBrands([newBrand, ...brands]);
    setShowAddModal(false);
    toast.success('הברנד נוסף בהצלחה');
    fetchFilters(); // Update stats
  };

  const handleBrandUpdated = (updatedBrand) => {
    setBrands(brands.map(b => b.id === updatedBrand.id ? updatedBrand : b));
    setShowEditModal(false);
    toast.success('הברנד עודכן בהצלחה');
  };

  const handleBrandDeleted = (brandId) => {
    setBrands(brands.filter(b => b.id !== brandId));
    setShowDeleteModal(false);
    setSelectedBrand(null);
    fetchFilters(); // Update stats
  };

  const handleToggleActive = async (brand) => {
    try {
      await axios.put(`/api/brands/${brand.id}`, {
        is_active: !brand.is_active
      });
      
      setBrands(brands.map(b => 
        b.id === brand.id ? { ...b, is_active: !b.is_active } : b
      ));
      
      toast.success(brand.is_active ? 'הברנד הושבת' : 'הברנד הופעל');
    } catch (error) {
      toast.error('שגיאה בעדכון סטטוס הברנד');
    }
  };

  const handleToggleFeatured = async (brand) => {
    try {
      await axios.put(`/api/brands/${brand.id}`, {
        is_featured: !brand.is_featured
      });
      
      setBrands(brands.map(b => 
        b.id === brand.id ? { ...b, is_featured: !b.is_featured } : b
      ));
      
      toast.success(brand.is_featured ? 'הברנד הוסר מהמומלצים' : 'הברנד נוסף למומלצים');
    } catch (error) {
      toast.error('שגיאה בעדכון סטטוס מומלץ');
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilterActive('all');
    setFilterFeatured('all');
    setSelectedCountry('');
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6" dir="rtl">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">ניהול ברנדים</h1>
            <p className="text-gray-600 mt-1">ניהול מותגים ולוגואים של המערכת</p>
          </div>
          
          <button
            onClick={handleAddBrand}
            className="flex items-center gap-2 bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition-colors shadow-lg"
          >
            <PlusIcon className="h-5 w-5" />
            <span className="font-semibold">ברנד חדש</span>
          </button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center gap-3">
                <div className="bg-blue-100 p-3 rounded-lg">
                  <BuildingOfficeIcon className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats.total_brands || 0}</p>
                  <p className="text-sm text-gray-600">סה"כ ברנדים</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center gap-3">
                <div className="bg-green-100 p-3 rounded-lg">
                  <CheckCircleIcon className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats.active_brands || 0}</p>
                  <p className="text-sm text-gray-600">ברנדים פעילים</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center gap-3">
                <div className="bg-yellow-100 p-3 rounded-lg">
                  <ChartBarIcon className="h-6 w-6 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats.featured_brands || 0}</p>
                  <p className="text-sm text-gray-600">ברנדים מומלצים</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <MagnifyingGlassIcon className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="חיפוש ברנד..."
              className="w-full pr-10 pl-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Active Filter */}
          <select
            value={filterActive}
            onChange={(e) => setFilterActive(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="all">כל הסטטוסים</option>
            <option value="active">פעילים בלבד</option>
            <option value="inactive">לא פעילים בלבד</option>
          </select>

          {/* Featured Filter */}
          <select
            value={filterFeatured}
            onChange={(e) => setFilterFeatured(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="all">כל הברנדים</option>
            <option value="featured">מומלצים בלבד</option>
            <option value="normal">רגילים בלבד</option>
          </select>

          {/* Country Filter */}
          <select
            value={selectedCountry}
            onChange={(e) => setSelectedCountry(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">כל המדינות</option>
            {countries.map(country => (
              <option key={country} value={country}>{country}</option>
            ))}
          </select>
        </div>

        {/* Clear Filters */}
        {(searchTerm || filterActive !== 'all' || filterFeatured !== 'all' || selectedCountry) && (
          <div className="mt-3 flex justify-end">
            <button
              onClick={clearFilters}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              נקה סינונים
            </button>
          </div>
        )}
      </div>

      {/* Brands Grid */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      ) : brands.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <BuildingOfficeIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">אין ברנדים להצגה</h3>
          <p className="text-gray-600 mb-4">התחל על ידי הוספת הברנד הראשון שלך</p>
          <button
            onClick={handleAddBrand}
            className="inline-flex items-center gap-2 bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700"
          >
            <PlusIcon className="h-5 w-5" />
            <span>הוסף ברנד</span>
          </button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {brands.map((brand) => (
              <div
                key={brand.id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow"
              >
                {/* Brand Logo */}
                <div className="h-40 bg-gray-100 flex items-center justify-center relative">
                  {brand.logo ? (
                    <img
                      src={brand.logo}
                      alt={brand.name}
                      className="max-h-full max-w-full object-contain p-4"
                    />
                  ) : (
                    <div className="text-center">
                      <PhotoIcon className="h-16 w-16 text-gray-300 mx-auto mb-2" />
                      <p className="text-sm text-gray-500">ללא לוגו</p>
                    </div>
                  )}
                  
                  {/* Badges */}
                  <div className="absolute top-2 right-2 flex flex-col gap-2">
                    {brand.is_featured && (
                      <span className="bg-yellow-500 text-white text-xs px-2 py-1 rounded-full font-semibold">
                        ⭐ מומלץ
                      </span>
                    )}
                    {!brand.is_active && (
                      <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full font-semibold">
                        לא פעיל
                      </span>
                    )}
                  </div>
                </div>

                {/* Brand Info */}
                <div className="p-4">
                  <h3 className="text-lg font-bold text-gray-900 mb-1 truncate" title={brand.name}>
                    {brand.name}
                  </h3>
                  
                  {brand.country && (
                    <div className="flex items-center gap-1 text-sm text-gray-600 mb-2">
                      <GlobeAltIcon className="h-4 w-4" />
                      <span>{brand.country}</span>
                    </div>
                  )}

                  <div className="flex items-center justify-between text-sm text-gray-600 mb-4">
                    <span>מוצרים: <strong>{brand.product_count || 0}</strong></span>
                  </div>

                  {/* Actions */}
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => handleEditBrand(brand)}
                      className="flex items-center justify-center gap-1 px-3 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                    >
                      <PencilIcon className="h-4 w-4" />
                      <span className="text-sm font-medium">עריכה</span>
                    </button>
                    
                    <button
                      onClick={() => handleDeleteBrand(brand)}
                      className="flex items-center justify-center gap-1 px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                    >
                      <TrashIcon className="h-4 w-4" />
                      <span className="text-sm font-medium">מחיקה</span>
                    </button>
                  </div>

                  {/* Toggle Buttons */}
                  <div className="mt-2 flex gap-2">
                    <button
                      onClick={() => handleToggleActive(brand)}
                      className={`flex-1 text-xs px-2 py-1.5 rounded ${
                        brand.is_active
                          ? 'bg-green-100 text-green-700 hover:bg-green-200'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {brand.is_active ? 'פעיל' : 'לא פעיל'}
                    </button>
                    
                    <button
                      onClick={() => handleToggleFeatured(brand)}
                      className={`flex-1 text-xs px-2 py-1.5 rounded ${
                        brand.is_featured
                          ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {brand.is_featured ? 'מומלץ' : 'רגיל'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="mt-6 flex justify-center items-center gap-2">
              <button
                onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }))}
                disabled={pagination.currentPage === 1}
                className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                הקודם
              </button>
              
              <span className="text-gray-700">
                עמוד {pagination.currentPage} מתוך {pagination.totalPages}
              </span>
              
              <button
                onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }))}
                disabled={pagination.currentPage === pagination.totalPages}
                className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                הבא
              </button>
            </div>
          )}
        </>
      )}

      {/* Modals */}
      {showAddModal && (
        <BrandFormModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onBrandCreated={handleBrandCreated}
        />
      )}

      {showEditModal && selectedBrand && (
        <BrandFormModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          brand={selectedBrand}
          onBrandUpdated={handleBrandUpdated}
        />
      )}

      {showDeleteModal && selectedBrand && (
        <DeleteBrandConfirmation
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          brand={selectedBrand}
          onBrandDeleted={handleBrandDeleted}
        />
      )}
    </div>
  );
};

export default AdminBrands;
